import React from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, Type } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { cn } from 'lib/utils';
import { READING_THEMES, READING_ZOOMS, type ReadingPrefs, type ReadingTheme } from './readingPrefs';

const THEME_SWATCH: Record<ReadingTheme, string> = {
  light: 'bg-white text-gray-900 border-gray-300',
  sepia: 'bg-[#F6EEDC] text-[#43341F] border-[#D9C9A6]',
  dark: 'bg-[#111827] text-gray-100 border-gray-700',
};

interface Props {
  prefs: ReadingPrefs;
  onTheme: (theme: ReadingTheme) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  className?: string;
}

type PanelProps = Omit<Props, 'className'>;

/** Theme swatches and text size, shared by the header popover and the phone menu. */
export function ReadingSettingsPanel({ prefs, onTheme, onZoomIn, onZoomOut }: PanelProps) {
  const { t } = useTranslation();
  const zooms = READING_ZOOMS as readonly number[];
  return (
    <>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('exam.reading.theme')}</p>
      <div className="mt-2 grid grid-cols-3 gap-2" role="radiogroup" aria-label={t('exam.reading.theme')}>
        {READING_THEMES.map((theme) => (
          <button
            key={theme}
            type="button"
            role="radio"
            aria-checked={prefs.theme === theme}
            onClick={() => onTheme(theme)}
            className={cn(
              'flex h-12 flex-col items-center justify-center rounded-lg border text-xs font-medium',
              THEME_SWATCH[theme],
              prefs.theme === theme && 'ring-2 ring-primary ring-offset-1',
            )}
          >
            <span className="font-serif text-base leading-none">Aa</span>
            {t(`exam.reading.theme_${theme}`)}
          </button>
        ))}
      </div>

      <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('exam.reading.size')}</p>
      <div className="mt-2 flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onZoomOut}
          disabled={prefs.zoom <= zooms[0]}
          aria-label={t('exam.reading.smaller')}
        >
          <Minus className="h-4 w-4" aria-hidden />
        </Button>
        <span className="tabular-nums text-sm font-semibold" aria-live="polite">
          {Math.round(prefs.zoom * 100)}%
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onZoomIn}
          disabled={prefs.zoom >= zooms[zooms.length - 1]}
          aria-label={t('exam.reading.larger')}
        >
          <Plus className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </>
  );
}

/** "Aa" in the exam header: page theme and text size for the whole section. */
export function ReadingSettings({ className, ...panel }: Props) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('exam.reading.settings')}
          title={t('exam.reading.settings')}
          className={cn('h-9 w-9 shrink-0', className)}
        >
          <Type className="h-4 w-4" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-4">
        <ReadingSettingsPanel {...panel} />
      </PopoverContent>
    </Popover>
  );
}
